import moment from 'moment';

// Helper đọc dữ liệu aggregate của HIS, dùng chung cho header, Tab1 và Tab3

// DateTime của HIS không kèm múi giờ → coi là giờ Việt Nam
export const hisTimeToUnix = (value) => {
  if (!value) return null;
  const raw = String(value);
  const m = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)
    ? moment(raw)
    : moment(raw).utcOffset('+07:00', true);
  return m.isValid() ? m.unix() : null;
};

// Thời điểm bản ghi HIS được cập nhật gần nhất
export const hisItemTime = (item) => hisTimeToUnix(item?.lastUpdatedAt || item?.createdAt);

export const maxHisItemTime = (items) => {
  const times = (items || []).map(hisItemTime).filter((t) => t !== null);
  return times.length ? Math.max(...times) : null;
};

// Lần khám mới nhất (theo ngày khám thực tế)
export const latestVisit = (his) => {
  const visits = [...(his?.visits || [])];
  visits.sort((a, b) => (hisTimeToUnix(b.admissionDate) || 0) - (hisTimeToUnix(a.admissionDate) || 0));
  return visits[0] || null;
};

// Đơn thuốc của lần khám; không có visit → đơn mới nhất (HIS đã sắp theo ngày khám giảm dần)
export const prescriptionsOfVisit = (his, visit) => {
  const list = his?.prescriptions || [];
  if (!visit || visit.id == null) return list.slice(0, 1);
  return list.filter((p) => p.visitId === visit.id);
};

// Thuốc của lần khám, bỏ trùng theo tên + hàm lượng
export const medicationsOfVisit = (his, visit) => {
  const list = his?.medications || [];
  const meds = !visit || visit.id == null ? list : list.filter((m) => m.visitId === visit.id);
  const seen = new Set();
  return meds.filter((m) => {
    const key = `${String(m.medicationName || '').trim().toLowerCase()}|${String(m.strength || '').trim().toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Tên (hàm lượng) – liều – tần suất – số lượng – số ngày – đường dùng / cách dùng
export const formatMedication = (m) => {
  const name = m.strength ? `${m.medicationName} (${m.strength})` : m.medicationName;
  const hasQty = m.quantityPrescribed !== null && m.quantityPrescribed !== undefined && m.quantityPrescribed !== '';
  const qty = hasQty ? [m.quantityPrescribed, m.unit].filter(Boolean).join(' ') : '';
  const days = m.daysSupply ? `${m.daysSupply} ngày` : '';
  return [name, m.dosage, m.frequency, qty, days, m.route, m.usageInstructions]
    .map((v) => (v === null || v === undefined ? '' : String(v).trim()))
    .filter(Boolean)
    .join(' – ');
};

export const extractFromNotes = (notes, label) => {
  // Lấy giá trị sau "label:" cho tới field kế tiếp (", Tên:") hoặc hết chuỗi
  const re = new RegExp(`${label}\\s*:\\s*([\\s\\S]*?)(?=,\\s*[^,:]+\\s*:|$)`, 'i');
  const match = String(notes || '').match(re);
  return match ? match[1].trim() : '';
};

// Bác sĩ + lời dặn lấy từ các đơn thuốc của lần khám
export const doctorAndNoteOf = (prescriptions) => {
  let doctor = '';
  let doctorAt = null;
  const notes = [];
  const withNotes = [];
  (prescriptions || []).forEach((p) => {
    if (!p.notes) return;
    const name = extractFromNotes(p.notes, 'Bác sĩ');
    if (name && !doctor) {
      doctor = name;
      doctorAt = hisItemTime(p);
    }
    const note = extractFromNotes(p.notes, 'Lời dặn') || p.notes.trim();
    if (note && !notes.includes(note)) notes.push(note);
    withNotes.push(p);
  });
  return { doctor, doctorAt, note: notes.join('\n'), noteAt: maxHisItemTime(withNotes) };
};

export const parseNumber = (value) => {
  const match = String(value ?? '').replace(',', '.').match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const isHba1cTest = (r) => [r?.testName, r?.testCode]
  .some((v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '').includes('hba1c'));

// HbA1c (%) mới nhất trong labResults; bỏ kết quả đơn vị mmol/mol
export const latestHba1c = (his) => {
  const items = (his?.labResults || [])
    .filter((r) => isHba1cTest(r) && !/mmol/i.test(r.resultUnit || ''))
    .map((r) => ({
      value: parseNumber(r.resultValue),
      at: hisItemTime(r),
      performedAt: hisTimeToUnix(r.performedDate) ?? hisItemTime(r),
    }))
    .filter((r) => r.value !== null);
  items.sort((a, b) => (b.performedAt || 0) - (a.performedAt || 0));
  return items[0] || null;
};
